<script lang="ts">
	import './modal.component.css';
	import { onMount } from 'svelte';
	import type { ModalAction } from '../../interfaces/ModalAction';
	import { _ } from '../../utility-services/Translation.service';
	import {
		VpModal,
		VpModalDialog,
		VpModalHeader,
		VpModalBody,
		VpModalFooter,
		VpModalCloseButton
	} from '@vtmn-play/svelte/VpModal';
	import { VpButton } from '@vtmn-play/svelte/VpButton';
	import { VpIcon } from '@vtmn-play/svelte/VpIcon';

	interface Props {
		show?: boolean;
		title: string;
		titleClass?: string;
		buttons?: ModalAction[];
		isLoading?: boolean;
		modalContentClass?: string;
		closeButton?: () => void;
		closable?: boolean;
		children?: import('svelte').Snippet;
	}

	let {
		show = $bindable(),
		title,
		titleClass = '',
		buttons = $bindable(),
		isLoading = false,
		modalContentClass = 'vpModal',
		closeButton,
		closable = $bindable(true),
		children
	}: Props = $props();
	let isModalReady = $state(false);
	let modalOpen = $state(false);

	onMount(() => {
		isModalReady = true;
	});

	$effect(() => {
		if (isModalReady) {
			modalOpen = Boolean(show);
		}
	});

	function handleClose() {
		closeButton?.();
		show = false;
	}

	function isLegacyIconClass(icon?: string): boolean {
		return !!icon && icon.startsWith('vtmx-');
	}
</script>

<VpModal bind:open={modalOpen} onclose={handleClose}>
	<VpModalDialog class={'vpModal ' + (modalContentClass ?? '')}>
		<VpModalHeader>
			<span class={titleClass}>{title}</span>
			{#if closable}
				<VpModalCloseButton disabled={isLoading} />
			{/if}
		</VpModalHeader>
		<VpModalBody class="vp-modal__body">
			{#if show}
				{@render children?.()}
			{/if}
		</VpModalBody>
		{#if (buttons?.length ?? 0) > 0}
			<VpModalFooter>
				<div class="modal-footer-actions">
					{#each buttons as btn}
						{#if btn.variant}
							<VpButton
								variant={btn.variant as any}
								disabled={btn.isDisabled || btn.isLoading}
								data-testid={btn.testId}
								onclick={() => btn.action()}
							>
								{#if !btn.isLoading}
									{#if btn.icon}
										{#if isLegacyIconClass(btn.icon)}
											<span aria-hidden="true" class="{btn.icon} vtmn-mr-1"></span>
										{:else}
											<VpIcon slot="startSlot" name={btn.icon as any} size={16} />
										{/if}
									{/if}
									{btn.title}
								{:else}
									<div
										class="vtmn-loader vtmn-loader_size--medium"
										style="color: var(--vtmn-semantic-color_content-accent);"
										data-testid="loader"
									>
										<span class="vtmn-sr-only">{$_('loading')}</span>
									</div>
								{/if}
							</VpButton>
						{:else}
							<button
								class={btn.className}
								style={btn.style}
								disabled={btn.isDisabled || btn.isLoading}
								data-testid={btn.testId}
								onclick={() => btn.action()}
							>
								{#if !btn.isLoading}
									{#if btn.icon}
										{#if isLegacyIconClass(btn.icon)}
											<span aria-hidden="true" class="{btn.icon} vtmn-mr-1"></span>
										{:else}
											<VpIcon class="vtmn-mr-1" name={btn.icon as any} size={16} />
										{/if}
									{/if}
									{btn.title}
								{:else}
									<div
										class="vtmn-loader vtmn-loader_size--medium"
										style="color: var(--vtmn-semantic-color_content-accent);"
										data-testid="loader"
									>
										<span class="vtmn-sr-only">{$_('loading')}</span>
									</div>
								{/if}
							</button>
						{/if}
					{/each}
				</div>
			</VpModalFooter>
		{/if}
	</VpModalDialog>
</VpModal>
